import type { MouseEvent } from 'react';
import React, { useMemo } from 'react';
import { withTheme } from 'styled-components';
import Modal from '@this/shared/modal/modal';
import { Link } from '@this/shared/ui/navigations/link';
import type OrderItem from '@this/domain/order_item2';
import { Checkbox } from '@this/shared/ui/inputs/checkbox';
import type Trip from '@this/domain/trip/trip2';
import { Button } from '@this/src/components/shared/ui/inputs/button';

interface Props {
  show: boolean;
  submitting: boolean;
  trip: Trip;
  orderItem: OrderItem | null;
  service: { name: string; tel: string };
  handleHide: () => void;
  handleSubmit: (e: MouseEvent<HTMLButtonElement>, orderItemId: number) => void;
  theme: { themeClass: string };
}

const CancelItemModal: React.FC<Props> = ({
  show,
  submitting,
  trip,
  orderItem,
  service,
  handleHide,
  handleSubmit,
  children,
  theme
}) => {
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);
  const [requestConfirmed, setRequestConfirmed] = React.useState(false);
  const styles = useMemo(() => getStyles(), []);

  return (
    <Modal show={show} hideModal={handleHide} maxWidth={1200} title="この予約を本当にキャンセル依頼しますか？">
      {trip.status === 5 ||
      (orderItem?.jobType === 'cancel' && orderItem.status && [7, 13].includes(orderItem.status)) ? (
        <p>この予約はキャンセル済みです</p>
      ) : trip.status === 6 ||
        (orderItem?.jobType === 'cancel' && orderItem.status && ![7, 13].includes(orderItem.status)) ? (
        <p>
          この予約はキャンセル対応中です。
          <br />
          対応が完了するまで少々 お待ち下さい。
        </p>
      ) : submitting ? (
        <p>処理中...</p>
      ) : (
        orderItem && (
          <div style={styles.modalContentWrapper} className="cancel-item-modal__modal-content-wrapper">
            <div style={styles.modalContent} className="cancel-item-modal__modal-content">
              <h1 style={styles.sectionTitle}>キャンセル依頼内容</h1>
              <div style={styles.travelerArea}>
                {orderItem.travelerInformations.map(traveler => (
                  <div key={traveler.id} style={styles.travelerItemArea}>
                    <p style={styles.nameLabel}>氏名</p>
                    <div style={styles.travelerItemNameArea}>
                      <div>{traveler.travelerName}</div>
                      {traveler.user?.email && <div>{traveler.user.email}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div style={styles.orderItemDetailArea}>
                {children}
                {theme.themeClass === 'tabikobo' && trip.foreignAir && (
                  <p style={styles.cancelFee}>
                    {`キャンセル手数料：${trip.cancelFeeText}`}
                    <br />
                    航空券発券後は、キャンセル手数料の他、キャンセル規程に従いキャンセル料が発生します。
                  </p>
                )}
              </div>
              <div style={styles.consentArea}>
                <h1 style={styles.sectionTitle}>以下の内容を必ずご確認ください</h1>
                <Checkbox checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}>
                  右の同意事項・注意事項に同意しました。
                </Checkbox>
                <Checkbox checked={requestConfirmed} onChange={e => setRequestConfirmed(e.target.checked)}>
                  キャンセル依頼内容を確認しました。
                </Checkbox>
              </div>
              <div style={styles.buttonArea}>
                <Button color="sub" onClick={handleHide}>
                  閉じる
                </Button>
                <Button
                  color="primary"
                  onClick={e => handleSubmit(e, orderItem.id)}
                  disabled={!agreedToTerms || !requestConfirmed || !trip.cancelRequestable}
                >
                  決定する
                </Button>
              </div>
              {!trip.cancelRequestable && (
                <div style={{ margin: '10px 0' }}>
                  <p>※ キャンセル依頼のリクエストは、ご利用前営業日18時まで可能です。</p>
                  <p>
                    ご利用当日のキャンセル依頼に関しましては、当画面の
                    <span style={{ fontWeight: 'bold' }}>[ご利用当日のキャンセル依頼］</span>
                    をご確認ください。
                  </p>
                </div>
              )}
            </div>
            <div style={styles.cancelConfirmPolicy} className="cancel-item-modal__cancel-confirm-policy">
              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>[ご利用当日のキャンセル依頼］</p>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '20px' }}>
                <li>
                  当日のキャンセルは、ご自身でご対応をお願いいたします。
                  {theme.themeClass !== 'tabikobo' && (
                    <>
                      （キャンセル方法は
                      <Link isExternal href="https://aitravel.jp/knowledges/000001075">
                        こちら
                      </Link>
                      をご確認ください）
                    </>
                  )}
                </li>
                <li>
                  ご自身でキャンセルした場合は、弊社にてご請求データを更新する必要がございます。キャンセルがお済みになりましたら、その旨チャットよりお知らせください。
                </li>
              </ul>

              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>[キャンセル料金について]</p>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '20px' }}>
                <li>キャンセルした場合、キャンセル規程に則りキャンセル料金が発生する場合がございます。</li>
                <li>各提携先の規程により、返金できない場合がございます。</li>
                <li>返金不可プラン等、キャンセル料金が100%発生している場合、返金はございません。</li>
                <li>キャンセルご依頼後、キャンセルの取り消しはできかねます。</li>
                <li>弊社事務手数料は、返金対象外でございます。</li>
                <li>旅程全体をキャンセルしたい場合、旅程詳細画面より一括キャンセル依頼をご利用ください。</li>
              </ul>

              <p style={{ marginBottom: '20px' }}>質問、不明点がある場合は下記までお問い合わせください。</p>
              <table>
                <tbody>
                  <tr>
                    <th>ツール</th>
                    <th>詳細</th>
                  </tr>
                  <tr>
                    <td>チャット</td>
                    <td>
                      <Link href={`/trips/${trip.id}`}>チャットへのリンク</Link>
                    </td>
                  </tr>
                  <tr>
                    <td>電話番号</td>
                    <td>{service.tel}</td>
                  </tr>
                  <tr>
                    <td>問い合わせフォーム</td>
                    <td>
                      <Link href="/inquiries/new">お問い合わせフォームへのリンク</Link>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p style={{ marginBottom: '40px' }}>
                ※平日18時以降、土日、祝日にご依頼いただいたキャンセル対応は翌営業日の対応になります。予めご了承くださいますようお願い申し上げます。
              </p>
            </div>
          </div>
        )
      )}
    </Modal>
  );
};

const getStyles = () => ({
  cancelFee: {
    marginBottom: 10
  },
  modalContentWrapper: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: 20
  },
  modalContent: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    flex: 6,
    minWidth: 0
  },
  cancelConfirmPolicy: {
    padding: 20,
    background: '#eee',
    flex: 4,
    minWidth: 0,
    width: 'auto'
  },
  consentArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 10,
    margin: '40px 0 20px'
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 10
  },
  travelerArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 10,
    marginBottom: 40
  },
  travelerItemArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 4
  },
  travelerItemNameArea: {
    display: 'flex' as const,
    flexDirection: 'row' as const,
    gap: 10
  },
  nameLabel: {
    fontSize: 14,
    fontWeight: 'bold' as const,
    lineHeight: 1.6
  },
  orderItemDetailArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const
  },
  buttonArea: {
    display: 'flex' as const,
    justifyContent: 'center' as const,
    gap: 20
  }
});

export default withTheme(CancelItemModal);
